import { useChat } from '@ai-sdk/react';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { memo, useEffect, useMemo, useRef } from 'react';
import { chatTransport } from '../../lib/transporters';
import { AGENTS } from '../../types';
import AIChatMessages from '../AIChatMessages';
import AIIcon from '../AIIcon';
import FloatingAIChatInput from './FloatingAIChatInput';

interface FloatingAIChatPanelProps {
  onClose: () => void;
  initialMessage?: string;
  threadId: string;
}

const FloatingAIChatPanel = ({ onClose, initialMessage, threadId }: FloatingAIChatPanelProps) => {
  const { t } = useTranslation('ai');
  const { getUser, getSession } = useUser();
  const { aiChat } = useFeatureFlags();
  const user = getUser();
  const session = getSession();

  const transport = useMemo(() => {
    const userId = user?.id ?? 'unknownUser';
    const fleetId = session?.fleet?.id ?? 'unknownFleet';
    const resourceId = `${userId}_${fleetId}` as const;
    return chatTransport({
      // aiChat flag ON → full fleet-manager assistant; OFF → dedicated
      // recommendations agent (narrow action pool). Replaces the old
      // scope='recommendations' hint now that each pool is its own agent.
      agentName: aiChat ? AGENTS.agentFleetManager : AGENTS.agentRecommendations,
      threadId,
      resourceId,
      source: 'web',
      component: 'floating',
      url: window.location.href,
    });
  }, [threadId, user?.id, session?.fleet?.id, aiChat]);

  const { messages, status, sendMessage, stop, regenerate } = useChat({
    id: threadId,
    transport,
    experimental_throttle: 100,
  });

  const initialMessageSentRef = useRef(false);

  useEffect(() => {
    if (initialMessage && status === 'ready' && !initialMessageSentRef.current) {
      initialMessageSentRef.current = true;
      void sendMessage({
        role: 'user',
        id: crypto.randomUUID(),
        parts: [{ type: 'text', text: initialMessage }],
      }).catch(() => {
        initialMessageSentRef.current = false;
      });
    }
  }, [initialMessage, status, sendMessage]);

  return (
    <div
      data-testid='floating-ai-chat-panel'
      className='fixed right-4 bottom-24 z-[1002] flex h-[min(600px,calc(100vh-8rem))] w-[min(440px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-gray-4 bg-white shadow-2xl'
    >
      <div className='flex items-center justify-between border-gray-4 border-b px-4 py-3'>
        <div className='flex items-center gap-2'>
          <AIIcon size={16} />
          <Typography variant='description-title'>{t('floating_chat.panel_title')}</Typography>
        </div>
        <Button variant='ghost' size='sm' icon={<Icon name='x' size='sm' />} onClick={onClose} aria-label={t('floating_chat.close_aria_label')} />
      </div>

      <div className='min-h-0 flex-1 overflow-y-auto'>
        {messages.length === 0 ? (
          <div className='flex h-full flex-col items-center justify-center gap-2 p-6 text-center'>
            <AIIcon size={32} />
            <Typography variant='description-title'>{t('floating_chat.welcome_title')}</Typography>
            <Typography variant='description-list-default' color='secondary'>
              {t('floating_chat.welcome_subtitle')}
            </Typography>
          </div>
        ) : (
          <AIChatMessages messages={messages} status={status} onRegenerate={regenerate} classNameContent='p-4' hideReasoning />
        )}
      </div>

      <FloatingAIChatInput sendMessage={sendMessage} status={status} stop={stop} />
    </div>
  );
};

export default memo(FloatingAIChatPanel);
